/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import EventEmitter from 'eventemitter3';

/**
 * @private
 * @typedef {import('../event-bus').EventBus<EventTypes>} EventBus
 * @typedef {import('../network/network-listener').EventTypes} EventTypes
 * @typedef {import('../network/network-listener').NetworkListener} NetworkListener
 */

/**
 * Naslouchač změnám v připojení k síti pro standardní webové API.
 * @implements {NetworkListener}
 */
class WebNetworkListener {
  /**
   * Vytvoří instanci.
   * @param {EventTarget} eventTarget Zdroj událostí `online` a `offline`.
   * @param {NavigatorOnLine} navigator Zdroj hodnoty `onLine`.
   */
  constructor(eventTarget, navigator) {
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    this.eventTarget = eventTarget;
    this.listenerMap = new Map();
    this.navigator = navigator;
    // Navázání naslouchání na instanci.
    this.onOffline = this.onOffline.bind(this);
    this.onOnline = this.onOnline.bind(this);
  }

  /** @type {NetworkListener['isOnline']} */
  isOnline() {
    return this.navigator.onLine;
  }

  /** @type {NetworkListener['off']} */
  off(type, callback) {
    const { eventTarget, listenerMap } = this;
    // Zatím je podporována pouze událost onlineChanged, ostatní jsou ignorovány.
    if (type !== 'onlineChanged') {
      return;
    }
    // Odebírání neexistujících callbacků je ignorováno.
    if (!listenerMap.has(callback)) {
      return;
    }
    // Vyhledá se odpovídající listener a odregistruje se.
    const { onOffline, onOnline } = listenerMap.get(callback);
    listenerMap.delete(callback);
    eventTarget.removeEventListener('offline', onOffline);
    eventTarget.removeEventListener('online', onOnline);
  }

  /** @type {NetworkListener['on']} */
  on(type, eventListener) {
    const { eventTarget, listenerMap } = this;
    // Zatím je podporována pouze událost onlineChanged, ostatní jsou ignorovány.
    if (type !== 'onlineChanged') {
      return;
    }
    // Opakované přidání stejného callbacku je ignorováno.
    if (listenerMap.has(eventListener)) {
      return;
    }
    // Vytvoří se nový listener a zaregistruje se.
    const onOffline = () => eventListener(false);
    const onOnline = () => eventListener(true);
    listenerMap.set(eventListener, { onOffline, onOnline });
    eventTarget.addEventListener('offline', onOffline);
    eventTarget.addEventListener('online', onOnline);
  }

  /**
   * Reakce na přechod do offline stavu.
   * @private
   */
  onOffline() {
    this.emitter.emit('onlineChanged', false);
  }

  /**
   * Reakce na přechod do online stavu.
   * @private
   */
  onOnline() {
    this.emitter.emit('onlineChanged', true);
  }
}

export default WebNetworkListener;
